import { useEffect, useState, type ReactElement } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Pencil, Plus, Trash2, Users } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';

import { Button } from '@/components/ui/button';
import { Select } from '@/components/ui/select';
import {
  DataTable,
  DataTableHeaderRow,
  DataTableTable,
  DataTableTbody,
  DataTableTd,
  DataTableTh,
  DataTableThead,
  DataTableTr,
} from '@/components/ui/data-table';
import {
  ClinicalSection,
  type ClinicalSectionLabels,
} from '@/features/patient-record/components/ClinicalSection';
import { ConfirmDeleteDialog } from '@/features/patient-record/components/_shared/ConfirmDeleteDialog';
import { RowActions, RowActionButton } from '@/features/patient-record/components/_shared/RowActions';
import { getFamilyMemberFullName } from '@/features/families/mappers';
import { cn } from '@/lib/utils';
import { useFamilyMemberMutations } from '@/features/family-record/hooks/useFamilyMemberMutations';
import { familyQueries } from '@/features/family-record/queries';
import type {
  CascadeContactStatus,
  FamilyMemberViewModel,
  FamilyViewModel,
} from '@/models/family';

import { AddMemberDialog } from './AddMemberDialog';
import { MemberEditDialog } from './MemberEditDialog';

const CONTACT_STATUSES: readonly CascadeContactStatus[] = [
  'NOT_CONTACTED',
  'CONTACTED',
  'TESTED',
  'DECLINED',
];

type PatientFilter = 'ALL' | 'PATIENT' | 'NON_PATIENT';
type ConnectedFilter = 'ALL' | 'CONNECTED' | 'NOT_CONNECTED';

interface MembersSectionProps {
  readonly family: FamilyViewModel;
}

/**
 * Family members — reworked to the patient record pattern (PTV2-1157): a
 * collapsible `ClinicalSection` with an eye/view-state summary and the "Add
 * member" affordance in the card header. The rich table (role, deceased,
 * inline contact-status) is preserved via the section's `customBody` slot;
 * per-row edit/delete use the shared `RowActions`.
 */
export function MembersSection({ family }: MembersSectionProps): ReactElement {
  const { t } = useTranslation();
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<FamilyMemberViewModel | null>(null);
  const [pendingDelete, setPendingDelete] = useState<FamilyMemberViewModel | null>(null);
  const [patientFilter, setPatientFilter] = useState<PatientFilter>('ALL');
  const [connectedFilter, setConnectedFilter] = useState<ConnectedFilter>('ALL');
  const mutations = useFamilyMemberMutations(family.guid);
  const membersQuery = useQuery(familyQueries.members(family.guid));
  const members: readonly FamilyMemberViewModel[] = membersQuery.data ?? [];
  // PTV2-1157 deep-link highlight: `?member=<personGuid>` (the "view in family" jump from a
  // non-patient individual's redirect screen) scrolls that member's row into view and flashes it.
  const [searchParams] = useSearchParams();
  const memberParam = searchParams.get('member');
  const [flashGuid, setFlashGuid] = useState<string | null>(null);
  useEffect(() => {
    if (memberParam === null || memberParam.length === 0 || membersQuery.data === undefined) {
      return;
    }
    const row = document.querySelector<HTMLElement>(`[data-member-guid="${CSS.escape(memberParam)}"]`);
    if (row === null) {
      return;
    }
    row.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setFlashGuid(memberParam);
    const timer = window.setTimeout(() => {
      setFlashGuid(null);
    }, 2600);
    return () => {
      window.clearTimeout(timer);
    };
  }, [memberParam, membersQuery.data]);
  const emptyCell = t('patient.demographics.emptyValue');
  const yesNo = (value: boolean): string =>
    value ? t('common.yes', { defaultValue: 'Yes' }) : t('common.no', { defaultValue: 'No' });

  // Client-side quick filters over the bounded member set, then a stable
  // patients-first sort. Applied to the rendered rows only — the query data
  // is never mutated.
  const filteredMembers = members.filter((member) => {
    const patientOk =
      patientFilter === 'ALL' ||
      (patientFilter === 'PATIENT' ? member.isPatient : !member.isPatient);
    const connectedOk =
      connectedFilter === 'ALL' ||
      (connectedFilter === 'CONNECTED'
        ? member.connectedInPedigree
        : !member.connectedInPedigree);
    return patientOk && connectedOk;
  });
  const displayedMembers = [...filteredMembers].sort(
    (a, b) => Number(b.isPatient) - Number(a.isPatient),
  );

  const labels: ClinicalSectionLabels = {
    title: t('routes.family.members.heading'),
    empty: t('routes.family.members.empty'),
    emptyIcon: <Users className="h-5 w-5" aria-hidden="true" />,
    loading: t('routes.family.members.loading'),
    errorHeading: t('routes.family.members.error.heading'),
    errorMessage: t('routes.family.members.error.message'),
    codeLabel: t('routes.patient.error.codeLabel'),
    retryLabel: t('routes.family.error.retry'),
  };

  return (
    <>
      <ClinicalSection
        id="members"
        labels={labels}
        query={membersQuery}
        summary={members.map((m) => ({
          label: getFamilyMemberFullName(m) ?? emptyCell,
          value: t(`family.contactStatus.${m.contactStatus}`),
          filled: true,
        }))}
        itemKey={(m) => m.id}
        // renderItem is unused because we own the populated body via customBody,
        // but the prop is required by the section contract.
        renderItem={() => null}
        headerAction={
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              setAdding(true);
            }}
          >
            <Plus className="h-3.5 w-3.5 mr-1.5" aria-hidden="true" />
            {t('routes.family.members.addMember')}
          </Button>
        }
        customBody={
          <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
              <SegmentedFilter
                label={t('routes.family.members.filters.patient.label', {
                  defaultValue: 'Patient',
                })}
                value={patientFilter}
                onChange={setPatientFilter}
                options={[
                  {
                    value: 'ALL',
                    label: t('routes.family.members.filters.all', { defaultValue: 'All' }),
                  },
                  {
                    value: 'PATIENT',
                    label: t('routes.family.members.filters.patient.only', {
                      defaultValue: 'Patient',
                    }),
                  },
                  {
                    value: 'NON_PATIENT',
                    label: t('routes.family.members.filters.patient.non', {
                      defaultValue: 'Non-patient',
                    }),
                  },
                ]}
              />
              <SegmentedFilter
                label={t('routes.family.members.filters.connected.label', {
                  defaultValue: 'Connected',
                })}
                value={connectedFilter}
                onChange={setConnectedFilter}
                options={[
                  {
                    value: 'ALL',
                    label: t('routes.family.members.filters.all', { defaultValue: 'All' }),
                  },
                  {
                    value: 'CONNECTED',
                    label: t('routes.family.members.filters.connected.only', {
                      defaultValue: 'Connected',
                    }),
                  },
                  {
                    value: 'NOT_CONNECTED',
                    label: t('routes.family.members.filters.connected.not', {
                      defaultValue: 'Not connected',
                    }),
                  },
                ]}
              />
            </div>
            <DataTable aria-label={t('routes.family.members.tableRegion')}>
            <DataTableTable>
              <DataTableThead>
                <DataTableHeaderRow>
                  <DataTableTh widthClassName="w-[28%] min-w-[7rem]">
                    {t('routes.family.members.table.name')}
                  </DataTableTh>
                  <DataTableTh widthClassName="w-20">
                    {t('routes.family.members.table.patient', { defaultValue: 'Patient' })}
                  </DataTableTh>
                  <DataTableTh widthClassName="w-28">
                    <span
                      title={t('routes.family.members.table.connectedInPedigree', {
                        defaultValue: 'Connected in pedigree',
                      })}
                    >
                      {t('routes.family.members.table.connected', { defaultValue: 'Connected' })}
                    </span>
                  </DataTableTh>
                  <DataTableTh widthClassName="w-28">
                    {t('routes.family.members.table.role')}
                  </DataTableTh>
                  <DataTableTh widthClassName="w-28">
                    {t('routes.family.members.table.deceased')}
                  </DataTableTh>
                  <DataTableTh widthClassName="min-w-40">
                    {t('routes.family.members.table.contactStatus')}
                  </DataTableTh>
                  <DataTableTh className="w-24" widthClassName="w-24" align="right">
                    <span className="sr-only">
                      {t('routes.family.members.table.actions')}
                    </span>
                  </DataTableTh>
                </DataTableHeaderRow>
              </DataTableThead>
              <DataTableTbody>
                {displayedMembers.map((member) => {
                  const name = getFamilyMemberFullName(member) ?? emptyCell;
                  const busy =
                    mutations.update.isPending &&
                    mutations.update.variables.memberId === member.id;
                  // A member connected to a node in the pedigree can't be removed from here (remove it
                  // from the pedigree instead); the proband is never removable. Non-connected /
                  // floating members remain deletable.
                  const deleteDisabled =
                    member.cascadeRole === 'PROBAND' || member.connectedInPedigree;
                  const deleteTitle =
                    member.cascadeRole === 'PROBAND'
                      ? t('routes.family.members.removeProbandDisabled', {
                          defaultValue: 'The proband cannot be removed',
                        })
                      : member.connectedInPedigree
                        ? t('routes.family.members.removeConnectedDisabled', {
                            defaultValue: 'A connected member can only be deleted from the pedigree',
                          })
                        : t('routes.family.members.removeAria', { name });
                  return (
                    <DataTableTr
                      key={member.id}
                      data-member-guid={member.personGuid ?? undefined}
                      className={cn(
                        'group transition-colors',
                        flashGuid !== null &&
                          member.personGuid === flashGuid &&
                          'bg-primary/10 ring-2 ring-inset ring-primary/60',
                      )}
                    >
                      {/* PTV2-1157: compact rows (~40% shorter than the shared default) — `py-1`
                          overrides the DataTable cell's `py-3`, scoped to this table only. */}
                      <DataTableTd className="py-1 font-medium text-foreground">
                        {member.isPatient && member.personGuid !== null ? (
                          <Link
                            to={`/patients/${member.personGuid}`}
                            className="text-primary hover:underline focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
                          >
                            {name}
                          </Link>
                        ) : (
                          name
                        )}
                      </DataTableTd>
                      <DataTableTd className="py-1 text-foreground/90">
                        {yesNo(member.isPatient)}
                      </DataTableTd>
                      <DataTableTd className="py-1 text-foreground/90">
                        {yesNo(member.connectedInPedigree)}
                      </DataTableTd>
                      <DataTableTd className="py-1 text-foreground/90">
                        {t(`family.cascadeRole.${member.cascadeRole}`)}
                      </DataTableTd>
                      <DataTableTd className="py-1">
                        <DeceasedBadge value={member.deceased} unknownLabel={emptyCell} />
                      </DataTableTd>
                      <DataTableTd className="py-1">
                        <Select
                          value={member.contactStatus}
                          disabled={busy}
                          aria-label={t('routes.family.members.contactStatusAria', {
                            name,
                          })}
                          onChange={(e) => {
                            const next = e.target.value as CascadeContactStatus;
                            if (next === member.contactStatus) return;
                            mutations.update.mutate({
                              memberId: member.id,
                              body: { contact_status: next },
                            });
                          }}
                          className="h-6 text-xs"
                        >
                          {CONTACT_STATUSES.map((s) => (
                            <option key={s} value={s}>
                              {t(`family.contactStatus.${s}`)}
                            </option>
                          ))}
                        </Select>
                      </DataTableTd>
                      <DataTableTd className="py-1" align="right">
                        <RowActions className="justify-end">
                          <RowActionButton
                            icon={<Pencil className="h-3.5 w-3.5" aria-hidden="true" />}
                            label={t('routes.family.members.editAria', { name })}
                            onClick={() => {
                              setEditing(member);
                            }}
                          />
                          <RowActionButton
                            icon={<Trash2 className="h-3.5 w-3.5 text-destructive" aria-hidden="true" />}
                            label={t('routes.family.members.removeAria', { name })}
                            title={deleteTitle}
                            onClick={() => {
                              setPendingDelete(member);
                            }}
                            disabled={deleteDisabled}
                          />
                        </RowActions>
                      </DataTableTd>
                    </DataTableTr>
                  );
                })}
              </DataTableTbody>
            </DataTableTable>
            </DataTable>
          </div>
        }
      />

      {adding && (
        <AddMemberDialog
          existingMembers={members}
          onCancel={() => {
            setAdding(false);
          }}
          onSubmit={(body) => {
            mutations.add.mutate(body, {
              onSuccess: () => {
                setAdding(false);
              },
            });
          }}
          busy={mutations.add.isPending}
        />
      )}

      {editing !== null && (
        <MemberEditDialog
          member={editing}
          busy={mutations.update.isPending}
          onCancel={() => {
            setEditing(null);
          }}
          onSubmit={(body) => {
            mutations.update.mutate(
              { memberId: editing.id, body },
              {
                onSuccess: () => {
                  setEditing(null);
                },
              },
            );
          }}
        />
      )}

      {pendingDelete !== null && (
        <ConfirmDeleteDialog
          title={t('routes.family.members.confirmRemove.title')}
          message={t('routes.family.members.confirmRemove.message', {
            name: getFamilyMemberFullName(pendingDelete) ?? emptyCell,
          })}
          confirmLabel={t('routes.family.members.confirmRemove.confirm')}
          busy={mutations.remove.isPending}
          onCancel={() => {
            setPendingDelete(null);
          }}
          onConfirm={() => {
            mutations.remove.mutate(
              { memberId: pendingDelete.id },
              {
                onSuccess: () => {
                  setPendingDelete(null);
                },
              },
            );
          }}
        />
      )}
    </>
  );
}

interface SegmentedFilterOption<T extends string> {
  readonly value: T;
  readonly label: string;
}

interface SegmentedFilterProps<T extends string> {
  readonly label: string;
  readonly value: T;
  readonly options: readonly SegmentedFilterOption<T>[];
  readonly onChange: (value: T) => void;
}

function SegmentedFilter<T extends string>({
  label,
  value,
  options,
  onChange,
}: SegmentedFilterProps<T>): ReactElement {
  return (
    <div className="flex items-center gap-1.5">
      <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </span>
      <div
        role="group"
        aria-label={label}
        className="inline-flex items-center rounded-md border border-border bg-card p-0.5"
      >
        {options.map((option) => {
          const active = option.value === value;
          return (
            <button
              key={option.value}
              type="button"
              aria-pressed={active}
              onClick={() => {
                onChange(option.value);
              }}
              className={cn(
                'h-6 rounded px-2 text-xs font-medium transition-colors focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1',
                active
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground',
              )}
            >
              {option.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

interface DeceasedBadgeProps {
  readonly value: boolean | null;
  readonly unknownLabel: string;
}

function DeceasedBadge({ value, unknownLabel }: DeceasedBadgeProps): ReactElement {
  const { t } = useTranslation();
  const styles =
    value === true
      ? 'border-border bg-muted text-muted-foreground'
      : value === false
        ? 'border-border bg-background text-foreground/80'
        : 'border-dashed border-border bg-transparent text-muted-foreground';
  const label =
    value === true
      ? t('routes.family.members.deceasedYes')
      : value === false
        ? t('routes.family.members.deceasedNo')
        : unknownLabel;
  return (
    <span
      className={`inline-flex items-center h-5 px-2 rounded-full border text-[10px] font-semibold uppercase tracking-wide ${styles}`}
    >
      {label}
    </span>
  );
}
